import { useCallback } from "react";
import { useGetProvinceAndMunicipalityList } from "@/hooks/useGetProvinceAndMunicipalityList";
import { TOption } from "@/types/common";

type UseGetMunicipalityCodeReturn = {
  getMunicipalityCode: (municipalityName: string) => string;
  getProvinceCode: (provinceName: string) => string;
  // visa 由来の文字列を study マスタの正規名へ寄せる (手動作成フォームの選択肢と同じ値にする)。
  getProvinceName: (provinceName: string) => string;
  getMunicipalityName: (municipalityName: string) => string;
  provinceOptions: TOption[];
};

// visa の番地付き市区町村文字列を市/区/町/村の境界で切り出し、マスタ突合用の名称にする。
const getActualMunicipalityName = (municipalityName: string): string => {
  const cuttingChars = ["市", "区", "町", "村"];

  const positions = cuttingChars.map((char) => municipalityName.indexOf(char)).filter((pos) => pos > 0);

  if (positions.length === 0) return municipalityName;

  const cityPos = municipalityName.indexOf("市");
  const districtPos = municipalityName.indexOf("区");

  if (cityPos !== -1 && districtPos !== -1 && cityPos < districtPos) {
    return municipalityName.slice(0, districtPos + 1);
  }

  return municipalityName.slice(0, Math.min(...positions) + 1);
};

export const useGetMunicipalityCode = (): UseGetMunicipalityCodeReturn => {
  const { municipalities, provinceOptions } = useGetProvinceAndMunicipalityList();

  const getMunicipalityCode = (municipalityName: string) =>
    municipalities?.find((item) => item.municipalityName === getActualMunicipalityName(municipalityName))?.municipalityCode?.toString() ?? "";

  const getProvinceCode = (provinceName: string) => provinceOptions?.find((item) => item.label === provinceName)?.value?.toString() ?? "";

  // 都道府県名をマスタの正規名へ。完全一致が無ければ元の値を返す (BE 側 fuzzy マッピングに委ねる)。
  const getProvinceName = useCallback(
    (provinceName: string) => provinceOptions?.find((item) => item.label === provinceName)?.label?.toString() ?? provinceName,
    [provinceOptions],
  );

  // 市区町村名をマスタの正規名へ。visa の番地付き文字列は市/区/町/村で切り出してから突合する。
  // マスタに無ければ切り出した値を返す (BE 側 fuzzy マッピングに委ねる)。
  const getMunicipalityName = useCallback(
    (municipalityName: string) => {
      const actual = getActualMunicipalityName(municipalityName);
      return municipalities?.find((item) => item.municipalityName === actual)?.municipalityName ?? actual;
    },
    [municipalities],
  );

  return { getMunicipalityCode, getProvinceCode, getProvinceName, getMunicipalityName, provinceOptions };
};
